// Convergence of a circular module (domain #35): the assessment logic of the copy-paste loop the
// executor runs — converged / continue / diverged / capped — and the range intersection test that
// triggers it after a write. Pure: no Office.js.
import { parseRange } from './a1';

export interface ConvergenceOptions {
  tolerance: number;
  maxIterations: number;
  /** A delta growing this many times in a row is divergence. */
  divergenceRuns?: number;
}

export type ConvergenceVerdict = 'converged' | 'continue' | 'diverged' | 'capped';

/** Verdict after reading the delta cell at the end of iteration `history.length`. */
export function assess(history: number[], opts: ConvergenceOptions): ConvergenceVerdict {
  const runs = opts.divergenceRuns ?? 3;
  const last = history[history.length - 1];
  if (!Number.isFinite(last)) return 'diverged';
  if (last <= opts.tolerance) return 'converged';
  if (history.length >= runs + 1) {
    let growing = 0;
    for (let i = history.length - 1; i > 0 && history[i] > history[i - 1]; i--) growing++;
    if (growing >= runs) return 'diverged';
  }
  if (history.length >= opts.maxIterations) return 'capped';
  return 'continue';
}

/** Do two A1 rectangles on the same sheet overlap? */
export function rangesIntersect(a: string, b: string): boolean {
  try {
    const A = parseRange(a);
    const B = parseRange(b);
    return !(A.end.row < B.start.row || B.end.row < A.start.row || A.end.col < B.start.col || B.end.col < A.start.col);
  } catch {
    return false;
  }
}

export interface CircularBlock {
  block_id: string;
  sheet: string;
  title?: string | null;
  range?: string | null;
  live_range: string;
  circ_range: string;
  switch_cell: string;
  delta_cell: string;
  tolerance: number;
  max_iterations: number;
}

/** Circular blocks a write touched (its `_changes` rectangle overlaps the block's range or live/circ rows). */
export function touchedCircularBlocks(blocks: CircularBlock[], change: { sheet: string; address: string } | null | undefined): CircularBlock[] {
  if (!change) return [];
  return blocks.filter((b) => b.sheet === change.sheet && [b.range, b.live_range, b.circ_range].some((r) => !!r && change.address.split(',').some((a) => rangesIntersect(a.trim(), r))));
}
